import { useState, useEffect, useRef } from 'react';

/**
 * パララックス効果を処理するカスタムフック
 * @param {Object} options - 設定オプション
 * @returns {Object} - パララックス制御用の状態と関数
 */
const useParallaxEffect = (options = {}) => {
  const {
    touchSensitivity = 0.05,
    gyroSensitivity = 2.0,
    decayRate = 0.95,
    maxTilt = 20,
  } = options;

  // 状態管理
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [targetRotation, setTargetRotation] = useState({ x: 0, y: 0 });
  
  // ドラッグ状態の参照
  const dragState = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    velocityX: 0,
    velocityY: 0,
  });
  
  // アニメーションフレーム参照
  const animationRef = useRef(null);

  // デバイス傾きイベントのハンドラー
  useEffect(() => {
    const handleOrientation = (e) => {
      if (dragState.current.isDragging) return;

      if (e.beta !== null && e.gamma !== null) {
        let beta = Math.max(-90, Math.min(90, e.beta));
        let gamma = Math.max(-90, Math.min(90, e.gamma));
        
        let newTargetX = 0;
        let newTargetY = 0;
        
        // 画面の向きに応じて調整
        if (window.orientation === 90) {
          newTargetX = -gamma * gyroSensitivity;
          newTargetY = beta * gyroSensitivity;
        } else if (window.orientation === -90) {
          newTargetX = gamma * gyroSensitivity;
          newTargetY = -beta * gyroSensitivity;
        } else if (window.orientation === 180) {
          newTargetX = -beta * gyroSensitivity;
          newTargetY = -gamma * gyroSensitivity;
        } else {
          newTargetX = beta * gyroSensitivity;
          newTargetY = gamma * gyroSensitivity;
        }
        
        // 最大傾き角度を制限
        newTargetX = Math.max(-maxTilt, Math.min(maxTilt, newTargetX));
        newTargetY = Math.max(-maxTilt, Math.min(maxTilt, newTargetY));
        
        setTargetRotation({ x: newTargetX, y: newTargetY });
      }
    };

    window.addEventListener('deviceorientation', handleOrientation);
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, [gyroSensitivity, maxTilt]);

  // タッチ操作とマウス操作のイベントハンドラー
  const handleStart = (clientX, clientY) => {
    dragState.current = {
      isDragging: true,
      startX: clientX,
      startY: clientY,
      lastX: clientX,
      lastY: clientY,
      velocityX: 0,
      velocityY: 0,
    };
  };

  const handleMove = (clientX, clientY) => {
    if (!dragState.current.isDragging) return;
    
    // 速度の計算
    dragState.current.velocityX = clientX - dragState.current.lastX;
    dragState.current.velocityY = clientY - dragState.current.lastY;
    
    // 方向を計算
    const deltaX = clientX - dragState.current.startX;
    const deltaY = clientY - dragState.current.startY;
    
    setTargetRotation({
      x: -deltaY * touchSensitivity,
      y: deltaX * touchSensitivity,
    });
    
    // 最後の位置を更新
    dragState.current.lastX = clientX;
    dragState.current.lastY = clientY;
  };

  const handleEnd = () => {
    if (!dragState.current.isDragging) return;
    
    // 慣性を適用
    setTargetRotation(prev => ({
      x: prev.x - dragState.current.velocityY * 0.1,
      y: prev.y + dragState.current.velocityX * 0.1,
    }));
    
    dragState.current.isDragging = false;
  };

  // イベントハンドラーのエクスポート
  const handlers = {
    // タッチイベント用
    onTouchStart: (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        handleStart(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    onTouchMove: (e) => {
      e.preventDefault();
      if (dragState.current.isDragging && e.touches.length === 1) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    onTouchEnd: () => handleEnd(),
    
    // マウスイベント用
    onMouseDown: (e) => handleStart(e.clientX, e.clientY),
    onMouseMove: (e) => handleMove(e.clientX, e.clientY),
    onMouseUp: () => handleEnd(),
    onMouseLeave: () => handleEnd(),
  };

  // アニメーションループ
  useEffect(() => {
    const updateAnimation = () => {
      setRotation(current => {
        // イージング処理: 現在の値を目標値に近づける
        const newX = current.x + (targetRotation.x - current.x) * 0.1;
        const newY = current.y + (targetRotation.y - current.y) * 0.1;
        
        return { x: newX, y: newY };
      });
      
      // ドラッグ終了後の減衰（慣性）
      if (!dragState.current.isDragging) {
        setTargetRotation(current => ({
          x: current.x * decayRate,
          y: current.y * decayRate,
        }));
      }
      
      // 継続的なアニメーションが必要かどうかチェック
      const isMovementSignificant = 
        Math.abs(targetRotation.x) > 0.01 || 
        Math.abs(targetRotation.y) > 0.01 || 
        Math.abs(rotation.x - targetRotation.x) > 0.01 || 
        Math.abs(rotation.y - targetRotation.y) > 0.01;
      
      if (isMovementSignificant || dragState.current.isDragging) {
        animationRef.current = requestAnimationFrame(updateAnimation);
      }
    };
    
    animationRef.current = requestAnimationFrame(updateAnimation);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetRotation, decayRate, rotation]);

  // 初期アニメーション
  useEffect(() => {
    const startInitialAnimation = () => {
      setTargetRotation({ x: 5, y: 5 });
      
      setTimeout(() => {
        setTargetRotation({ x: 0, y: 0 });
      }, 2000);
    };
    
    startInitialAnimation();
  }, []);

  return {
    rotation,
    handlers,
    setTargetRotation,
  };
};

export default useParallaxEffect;
